/* Auth Store - Pinia */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/client'
import type { User, AuthState } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const apiTokens = ref<Array<{ id: number; name: string; expires_at: number | null; last_used_at: number | null; created_at: number }>>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')

  function setAuth(authData: { token: string; user: User }) {
    token.value = authData.token
    user.value = authData.user
    localStorage.setItem('auth_token', authData.token)
    localStorage.setItem('auth_user', JSON.stringify(authData.user))
  }

  function clearAuth() {
    token.value = null
    user.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
  }

  async function login(username: string, password: string) {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.login(username, password)
      setAuth({ token: response.token, user: response.user })
      return response
    } catch (e: any) {
      error.value = e.message || 'Error al iniciar sesión'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function register(data: { username: string; email: string; password: string }) {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.register(data)
      setAuth({ token: response.token, user: response.user })
      return response
    } catch (e: any) {
      error.value = e.message || 'Error al registrarse'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function fetchMe() {
    if (!token.value) return false
    
    loading.value = true
    try {
      const response = await authApi.me()
      user.value = response.user
      localStorage.setItem('auth_user', JSON.stringify(response.user))
      return true
    } catch (e: any) {
      // Token might be expired
      clearAuth()
      return false
    } finally {
      loading.value = false
    }
  }

  async function createApiToken(name: string, expiresAt?: number) {
    loading.value = true
    error.value = null
    try {
      return await authApi.createApiToken(name, expiresAt)
    } catch (e: any) {
      error.value = e.message || 'Error creando token API'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function listApiTokens() {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.listApiTokens()
      apiTokens.value = response.tokens || []
      return response.tokens
    } catch (e: any) {
      error.value = e.message || 'Error listando tokens'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function revokeApiToken(id: number) {
    loading.value = true
    error.value = null
    try {
      await authApi.revokeApiToken(id)
      apiTokens.value = apiTokens.value.filter(t => t.id !== id)
    } catch (e: any) {
      error.value = e.message || 'Error revocando token'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    loading.value = true
    error.value = null
    try {
      return await authApi.changePassword(currentPassword, newPassword)
    } catch (e: any) {
      error.value = e.message || 'Error cambiando contraseña'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function deleteAccount(confirmation: string) {
    loading.value = true
    error.value = null
    try {
      return await authApi.deleteAccount(confirmation)
    } catch (e: any) {
      error.value = e.message || 'Error eliminando cuenta'
      throw e
    } finally {
      loading.value = false
    }
  }

  function initAuth() {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('auth_user')
    
    if (storedToken && storedUser) {
      try {
        token.value = storedToken
        user.value = JSON.parse(storedUser)
        return true
      } catch {
        clearAuth()
        return false
      }
    }
    return false
  }

  function clearError() {
    error.value = null
  }

  function logout() {
    clearAuth()
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    // Actions
    setAuth,
    clearAuth,
    login,
    register,
    fetchMe,
    createApiToken,
    listApiTokens,
    revokeApiToken,
    initAuth,
    clearError,
    logout,
  }
})